import VoxelStatistics from '../../enums/VoxelStatistics.js';
import { boundsOfGrid, containsBounds, intersectBounds, mapBoundsBetweenGrids, volumeOfBounds, voxelCountOfGrid, } from '../../utilities/voxelGrid/index.js';
export function isMutableSlab(slot) {
    return slot.mutability === 'owned';
}
export class MutableVolumeTextureSlab {
    constructor({ id, ownerId, grid, statistic = VoxelStatistics.Average, bytes, front, back, applyGrid, }) {
        this.mutability = 'owned';
        this.dirty = [];
        this.drawDepth = 0;
        this.pendingFill = false;
        this.id = id;
        this.ownerId = ownerId;
        this.statistic = statistic;
        this.bytes = bytes * 2;
        this.frontTexture = front;
        this.backTexture = back;
        this.frontGrid = grid;
        this.backGrid = grid;
        this.applyGrid = applyGrid;
        this.applyGrid(front, grid);
        this.applyGrid(back, grid);
    }
    get grid() {
        return this.frontGrid;
    }
    get pendingGrid() {
        return this.backGrid;
    }
    get inFlight() {
        return this.drawDepth > 0;
    }
    get hasPendingFill() {
        return this.pendingFill;
    }
    retarget(grid) {
        this.backGrid = grid;
        this.applyGrid(this.backTexture, grid);
        this.dirty = [boundsOfGrid(grid)];
        this.pendingFill = true;
    }
    writeTarget() {
        return this.backTexture;
    }
    publish() {
        if (this.inFlight) {
            return false;
        }
        this.exchange();
        return true;
    }
    beginRender() {
        this.drawDepth++;
        return this.frontTexture;
    }
    endRender() {
        this.drawDepth = Math.max(this.drawDepth - 1, 0);
        if (this.drawDepth === 0 && this.pendingFill) {
            this.exchange();
        }
    }
    exchange() {
        if (!this.pendingFill) {
            return;
        }
        const texture = this.frontTexture;
        const grid = this.frontGrid;
        this.frontTexture = this.backTexture;
        this.frontGrid = this.backGrid;
        this.backTexture = texture;
        this.backGrid = grid;
        this.pendingFill = false;
    }
}
export class VolumeTextureSet {
    constructor(description) {
        this.references = 0;
        this.pinned = false;
        this.lastUsed = 0;
        this.slots = [];
        this.slotsById = new Map();
        this.regions = new Map();
        this.buckets = new Map();
        this.bucketSize = [1, 1, 1];
        this.name = description.name;
        this.volumeGrid = description.volumeGrid;
        this.statistic = description.statistic ?? VoxelStatistics.Average;
        this.coverage = description.coverage ?? 'full-extent';
        this.spacing = description.spacing;
        this.backstop = description.backstop ?? false;
    }
    members() {
        return [...this.slots];
    }
    get size() {
        return this.slots.length;
    }
    get bytes() {
        return this.slots.reduce((total, slot) => total + slot.bytes, 0);
    }
    get(id) {
        return this.slotsById.get(id);
    }
    add(slot) {
        this.slots.push(slot);
        this.slotsById.set(slot.id, slot);
        this.indexSlot(slot);
        return slot;
    }
    membersCovering(region) {
        if (!region || this.slots.length <= 1) {
            return this.members();
        }
        const found = new Set();
        for (const key of this.bucketKeysOf(region)) {
            const bucket = this.buckets.get(key);
            if (!bucket) {
                continue;
            }
            for (const slot of bucket) {
                if (found.has(slot)) {
                    continue;
                }
                const slotRegion = this.regions.get(slot.id);
                if (slotRegion && volumeOfBounds(intersectBounds(slotRegion, region))) {
                    found.add(slot);
                }
            }
        }
        return [...found];
    }
    markDirty(bounds, onSlice) {
        for (const slot of this.membersCovering(bounds)) {
            const grid = isMutableSlab(slot) ? slot.pendingGrid : slot.grid;
            const region = intersectBounds(mapBoundsBetweenGrids(this.volumeGrid, grid, bounds), boundsOfGrid(grid));
            if (volumeOfBounds(region) === 0) {
                continue;
            }
            if (onSlice) {
                for (let slice = region[2][0]; slice <= region[2][1]; slice++) {
                    onSlice(slot, slice);
                }
            }
            addRegion(slot, region);
        }
    }
    markAllDirty(onSlice) {
        for (const slot of this.slots) {
            const grid = isMutableSlab(slot) ? slot.pendingGrid : slot.grid;
            const region = boundsOfGrid(grid);
            if (onSlice) {
                for (let slice = region[2][0]; slice <= region[2][1]; slice++) {
                    onSlice(slot, slice);
                }
            }
            addRegion(slot, region);
        }
    }
    indexSlot(slot) {
        const grid = isMutableSlab(slot) ? slot.pendingGrid : slot.grid;
        const region = intersectBounds(mapBoundsBetweenGrids(grid, this.volumeGrid, boundsOfGrid(grid)), boundsOfGrid(this.volumeGrid));
        this.regions.set(slot.id, region);
        const extent = [0, 1, 2].map((axis) => Math.max(1, region[axis][1] - region[axis][0] + 1));
        if (this.slots.length === 1) {
            this.bucketSize = extent;
        }
        else {
            const widened = [0, 1, 2].map((axis) => Math.max(this.bucketSize[axis], extent[axis]));
            if (widened.some((size, axis) => size !== this.bucketSize[axis])) {
                this.bucketSize = widened;
                this.rebuildIndex();
                return;
            }
        }
        this.putInBuckets(slot, region);
    }
    rebuildIndex() {
        this.buckets.clear();
        for (const slot of this.slots) {
            this.putInBuckets(slot, this.regions.get(slot.id));
        }
    }
    putInBuckets(slot, region) {
        if (!region) {
            return;
        }
        for (const key of this.bucketKeysOf(region)) {
            const bucket = this.buckets.get(key);
            if (bucket) {
                bucket.push(slot);
            }
            else {
                this.buckets.set(key, [slot]);
            }
        }
    }
    *bucketKeysOf(region) {
        const [width, height, depth] = this.bucketSize;
        const first = [
            Math.floor(region[0][0] / width),
            Math.floor(region[1][0] / height),
            Math.floor(region[2][0] / depth),
        ];
        const last = [
            Math.floor(region[0][1] / width),
            Math.floor(region[1][1] / height),
            Math.floor(region[2][1] / depth),
        ];
        for (let k = first[2]; k <= last[2]; k++) {
            for (let j = first[1]; j <= last[1]; j++) {
                for (let i = first[0]; i <= last[0]; i++) {
                    yield `${i},${j},${k}`;
                }
            }
        }
    }
}
function addRegion(slot, region) {
    if (slot.dirty.some((existing) => containsBounds(existing, region))) {
        return;
    }
    slot.dirty = slot.dirty.filter((existing) => !containsBounds(region, existing));
    slot.dirty.push(region);
}
export function voxelsOfGrid(grid) {
    return voxelCountOfGrid(grid);
}
